import { HttpClient } from '@angular/common/http';
import {
  Directive,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { IMAGE_RESIZE_URL } from '@libs/shared/tokens/image-resize-url.token';

@Directive({
  selector: 'img[appImageFallbackResize]',
  standalone: true,
})
export class ImageFallbackResizeDirective implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly el = inject(ElementRef<HTMLImageElement>);
  private readonly imageResizeUrl = inject(IMAGE_RESIZE_URL);
  private readonly destroy$ = new Subject<void>();
  private retryAttempted = false;
  private originalSrc: string | null = null;

  @HostListener('error')
  onError() {
    // 이미 재시도했거나 src가 없으면 중단
    if (this.retryAttempted || !this.el.nativeElement.src) {
      return;
    }

    const currentSrc = this.el.nativeElement.src;
    this.originalSrc = currentSrc;

    // S3 URL 파싱
    const resizeUrl = this.buildResizeUrl(currentSrc);
    if (!resizeUrl) {
      return;
    }

    this.retryAttempted = true;

    // 리사이징 서비스 호출
    this.http
      .get(resizeUrl, { observe: 'response', responseType: 'blob' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.status === 200) {
            // 성공 시 원본 URL로 재시도
            console.log('[ImageFallbackResize] Resize triggered, retrying...');
            this.reloadImage();
          }
        },
        error: err => {
          console.error('[ImageFallbackResize] Resize service failed:', err);
        },
      });
  }

  private buildResizeUrl(imageUrl: string): string | null {
    try {
      const url = new URL(imageUrl);

      // S3 key 추출
      // 예: https://cdn.sprintapp.co/media/images/300x300/{uuid}/photo.jpg
      const pathParts = url.pathname.split('/').filter(Boolean);

      // pathname에서 key 추출 (media/images/300x300/... 형태)
      const key = pathParts.join('/');

      if (!key) {
        console.warn('[ImageFallbackResize] Could not extract key');
        return null;
      }

      return `${this.imageResizeUrl}/?key=${encodeURIComponent(key)}`;
    } catch (e) {
      console.error('[ImageFallbackResize] Invalid URL:', e);
      return null;
    }
  }

  private reloadImage() {
    if (!this.originalSrc) {
      return;
    }

    const img = this.el.nativeElement;

    // 캐시 무효화를 위해 timestamp 추가
    const timestamp = new Date().getTime();
    const separator = this.originalSrc.includes('?') ? '&' : '?';
    const newSrc = `${this.originalSrc}${separator}_retry=${timestamp}`;

    // 200 응답 = 리사이징 완료, 바로 재시도
    img.src = newSrc;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
